import type { ReactNode } from 'react';
import type { NavigationOutcome, NavigationRequest } from '../../../shared/lib/setupRevision';
import { useNavigationGuard } from '../../../shared/ui/navigation';

interface UseUnsavedTabChangeGuardArgs {
  /** Whether the mapping configuration store holds unsaved edits. */
  isDirty: boolean;
  /** Whether a saved profile exists that Discard could restore the edits to. */
  hasVerifiedBaseline: boolean;
  /** Persists the pending edits; resolves true when they were saved. */
  onSave: () => Promise<boolean>;
  /** Reverts the pending edits to the saved profile state; resolves true when reverted. */
  onDiscard: () => Promise<boolean>;
}

export interface UnsavedTabChangeGuardApi {
  /** Asks the shared guard for a destination and runs `commit` only if it grants it. */
  requestNavigation: (
    request: NavigationRequest,
    commit: () => void
  ) => Promise<NavigationOutcome>;
  unsavedTabChangeDialog: ReactNode;
  /** True while a request is waiting on the user or on a save/discard operation. */
  isNavigationPending: boolean;
}

/**
 * Connects the mapping workspace to the one navigation guard: it supplies the
 * feature's dirty state and save/discard operations, and renames the guard's
 * dialog to the field the workspace content model already renders.
 */
export function useUnsavedTabChangeGuard({
  isDirty,
  hasVerifiedBaseline,
  onSave,
  onDiscard,
}: UseUnsavedTabChangeGuardArgs): UnsavedTabChangeGuardApi {
  const { requestNavigation, navigationGuardDialog, isNavigationPending } = useNavigationGuard({
    isDirty,
    hasVerifiedBaseline,
    onSave,
    onDiscard,
  });

  return {
    requestNavigation,
    unsavedTabChangeDialog: navigationGuardDialog,
    isNavigationPending,
  };
}
